import {createContext, use, useCallback, useMemo} from 'react'
import {useQueryClient} from '@github-ui/react-query'
import type {APITask} from '../../../types/api-task'
import type {Session} from '../../../types/session'
import {channelHistoryRefetchInterval} from '../../../utils/channel-session'
import {generateAhpEventsQueryKey} from '../../../utils/fetch-ahp-session-events'
import {useTaskAliveUpdates} from '../../../hooks/use-task-alive-updates'
import {AhpSessionHistoryTimeline, type AhpSessionHistoryHeaderContext} from './AhpSessionHistoryTimeline'
import {ChannelSessionAttribution} from './ChannelSessionAttribution'
import {extractTurnMessageType, type ShouldShowUserMessage} from './turn-meta'
import type {ContributedInstructionsMetadata} from '@github-ui/ahp-session-client/contracts'

const shouldShowChannelUserMessage: ShouldShowUserMessage = message => extractTurnMessageType(message) !== 'system'

interface ChannelSessionHeaderContextValue {
  session: Session
  creatorType?: APITask['creator_type']
}

const ChannelSessionHeaderContext = createContext<ChannelSessionHeaderContextValue | undefined>(undefined)

function ChannelSessionHeader({chatState, profiles}: AhpSessionHistoryHeaderContext) {
  const context = use(ChannelSessionHeaderContext)
  if (!context) throw new Error('Channel session headers must be rendered within ChannelSessionHistoryTimeline.')

  return (
    <ChannelSessionAttribution
      session={context.session}
      chatState={chatState}
      profiles={profiles}
      creatorType={context.creatorType}
    />
  )
}

export function ChannelSessionHistoryTimeline({
  session,
  taskAliveChannel,
  creatorType,
  onContributedInstructions,
  loadingPresentation = 'local',
}: {
  session: Session
  taskAliveChannel?: string
  creatorType?: APITask['creator_type']
  onContributedInstructions?: (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => void
  loadingPresentation?: 'local' | 'page'
}) {
  const queryClient = useQueryClient()
  const invalidateEvents = useCallback(() => {
    if (session.id) {
      queryClient.invalidateQueries({queryKey: generateAhpEventsQueryKey(session.id)})
    }
  }, [queryClient, session.id])
  useTaskAliveUpdates(taskAliveChannel, invalidateEvents)
  const headerContext = useMemo(() => ({session, creatorType}), [creatorType, session])

  return (
    <ChannelSessionHeaderContext value={headerContext}>
      <AhpSessionHistoryTimeline
        session={session}
        ariaLabel="Channel session history"
        refetchInterval={channelHistoryRefetchInterval(session)}
        shouldShowUserMessage={shouldShowChannelUserMessage}
        HeaderComponent={ChannelSessionHeader}
        onContributedInstructions={onContributedInstructions}
        loadingPresentation={loadingPresentation}
      />
    </ChannelSessionHeaderContext>
  )
}
